import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import type { IMessage } from '@/types/admin/messages';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { isMessageSentByUser, formatSenderName } from '@/utils/residents/messages/messageUtils';
import { formatMessageTime } from '@/utils/common/formatting';

export const MessageBubbleMobile: React.FC<{ message: IMessage; authUserId?: number }> = ({ message, authUserId }) => {
  const { colors, components } = useThemeColors();
  const sentByYou = isMessageSentByUser(message.sender?.id, authUserId, message.isAdmin);

  // Sent bubbles are always blue, so their text/labels are always light.
  // Received bubbles follow the theme.
  const sentTextColor = components.button.primaryText;
  const sentMutedColor = 'rgba(255,255,255,0.8)';
  const sentTimestampColor = 'rgba(255,255,255,0.6)';

  const bubbleStyle = {
    alignSelf: sentByYou ? 'flex-end' as const : 'flex-start' as const,
    backgroundColor: sentByYou ? components.chat.sentBubble : components.chat.receivedBubble,
    borderTopLeftRadius: sentByYou ? 14 : 4,
    borderTopRightRadius: sentByYou ? 4 : 14,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  };

  const nameString = sentByYou
    ? formatSenderName(message, sentByYou, 'mobile')
    : message.isAdmin
      ? formatSenderName(message, sentByYou, 'mobile')
      : '';

  const bodyColor = sentByYou ? sentTextColor : colors.text.primary;
  const nameColor = sentByYou ? sentMutedColor : colors.text.muted;
  const timestampColor = sentByYou ? sentTimestampColor : components.chat.timestamp;

  return (
    <View style={[styles.messageBubble, bubbleStyle]}>
      {nameString && (
        <Text style={[styles.senderName, { color: nameColor }]}>
          {nameString}
        </Text>
      )}
      {!!message.content && <Text style={[styles.messageText, { color: bodyColor }]}>{message.content}</Text>}
      <Text style={[styles.timeStamp, { color: timestampColor }]}>
        {formatMessageTime(message.createdAt)}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginVertical: 4,
    gap: 2,
  },
  senderName: {
    ...REDESIGN_TYPOGRAPHY.caption,
    fontWeight: '500',
    marginBottom: 2,
  },
  messageText: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  timeStamp: {
    ...REDESIGN_TYPOGRAPHY.caption,
    fontWeight: '300',
    marginTop: 2,
    textAlign: 'right',
  },
});

export default MessageBubbleMobile;
